import React from "react";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { STEP_KEYS, STEP_LABELS, stateLabel, stepTone } from "@/steps";

import { blockedDays, handOwner, sharedNames, STALE_DAYS } from "./pipeline";

const TONE = {
  done: "text-muted-foreground",
  current: "rounded bg-primary/10 px-1.5 py-0.5 font-medium text-foreground",
  todo: "text-muted-foreground/60",
  error: "rounded bg-destructive/10 px-1.5 py-0.5 font-medium text-destructive",
};

function cellText(key, step) {
  if (!step) return "—";
  if (key === "recit" && step.state === "redige" && step.version) {
    return `v${step.version}`;
  }
  if (key === "bd" && step.state !== "non_demarree") {
    return `${step.validated}/${step.expected}`;
  }
  return stateLabel(key, step);
}

export default function ClientsTable({
  clients,
  usersById = {},
  onOpen,
  renderActions,
  now = Date.now(),
}) {
  const stopPropagation = (event) => event.stopPropagation();

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <Table>
        <TableHeader className="bg-card">
          <TableRow>
            <TableHead>Client</TableHead>
            {STEP_KEYS.map((key) => (
              <TableHead key={key}>{STEP_LABELS[key]}</TableHead>
            ))}
            <TableHead>Bloqué depuis</TableHead>
            <TableHead>Qui a la main</TableHead>
            <TableHead aria-label="Actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client) => {
            const steps = client.steps || {};
            const days = blockedDays(steps.blocked_since, now);
            const shared = sharedNames(client, usersById);
            const isStale = days !== null && days >= STALE_DAYS;
            const canOpen = client.can_open !== false;
            const createdTitle = client.created_at
              ? `Créé le ${new Date(client.created_at).toLocaleDateString("fr-FR")}`
              : null;
            const accessTitle = canOpen
              ? null
              : `Dossier de ${usersById[client.owner_id]?.name || "—"} — non partagé avec toi`;
            const clientTitle = [accessTitle, createdTitle].filter(Boolean).join(" · ");

            return (
              <TableRow
                key={client.session_id}
                role={canOpen ? "link" : undefined}
                tabIndex={canOpen ? 0 : undefined}
                data-testid={`row-${client.session_id}`}
                data-can-open={canOpen ? undefined : "false"}
                className={canOpen ? "cursor-pointer" : undefined}
                onClick={canOpen ? () => onOpen(client) : undefined}
                onKeyDown={canOpen ? (event) => {
                  if (event.key === "Enter") onOpen(client);
                } : undefined}
              >
                <TableCell title={clientTitle || undefined}>
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        client.prenom_nom
                          ? `font-medium${canOpen ? "" : " text-muted-foreground"}`
                          : "italic text-muted-foreground"
                      }
                    >
                      {client.prenom_nom || "Client sans nom"}
                    </span>
                    {client.is_shared && <Badge variant="secondary">Partagé</Badge>}
                  </div>
                  {client.activite && (
                    <div className="text-xs text-muted-foreground">{client.activite}</div>
                  )}
                </TableCell>
                {STEP_KEYS.map((key) => {
                  const step = steps[key];
                  const tone = stepTone(key, step, steps.current);
                  const text = cellText(key, step);
                  const isLink = key === "minisite" && step?.url;

                  return (
                    <TableCell key={key} data-testid={`cell-${key}`} data-tone={tone}>
                      {isLink ? (
                        <a
                          href={step.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={stopPropagation}
                          onKeyDown={stopPropagation}
                          className={`${TONE[tone]} underline`}
                        >
                          {text}
                        </a>
                      ) : (
                        <span className={TONE[tone]}>{text}</span>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell
                  data-testid="cell-blocked"
                  data-stale={isStale ? "true" : undefined}
                  className={isStale ? "font-medium text-amber-700" : undefined}
                  title={
                    steps.blocked_since
                      ? new Date(steps.blocked_since).toLocaleString("fr-FR")
                      : undefined
                  }
                >
                  {days === null ? "—" : `${days} j`}
                </TableCell>
                <TableCell
                  data-testid="cell-hand"
                  title={shared.length ? `Partagé avec ${shared.join(", ")}` : undefined}
                >
                  {handOwner(client, usersById)}
                </TableCell>
                <TableCell
                  onClick={stopPropagation}
                  onKeyDown={stopPropagation}
                  className="text-right"
                >
                  {renderActions ? renderActions(client) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
